import { Component, OnDestroy, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AdminConfigService } from '../../services/admin-config.service';
import { ToastNotificationComponent } from '../../toast-notification/toast-notification.component';
import { AcsCustomer } from '../models/acs.models';
import { ACS_TOAST_SUCCESS_DURATION, ACS_TOAST_ERROR_DURATION } from '../utils/acs.constants';
import { extractErrorMessage, executeBulkDelete, formatBulkDeleteToast } from '../utils/acs.utils';

@Component({
  selector: 'app-acs-delete-customer',
  templateUrl: './acs-delete-customer.component.html',
  styleUrls: ['../acs-shared.css'],
  standalone: false
})
export class AcsDeleteCustomerComponent implements OnDestroy {

  private _destroy$: Subject<void> = new Subject<void>();

  @ViewChild('toastNotification') toastNotification!: ToastNotificationComponent;

  customers: AcsCustomer[] = [];
  searchTerm: string = '';
  hasSearched: boolean = false;

  selectedIds: Set<number> = new Set();
  showBulkDeleteConfirm: boolean = false;
  isBulkDeleting: boolean = false;

  constructor(
    private _adminConfigService: AdminConfigService
  ) {}

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  searchCustomers(): void {
    this.selectedIds = new Set();
    this.hasSearched = true;
    if (!this.searchTerm) return;
    this._adminConfigService.searchCustomers(this.searchTerm).pipe(takeUntil(this._destroy$)).subscribe({
      next: (data) => this.customers = data,
      error: () => this.toastNotification.showToast('Failed to search customers', ACS_TOAST_ERROR_DURATION, 'error')
    });
  }

  // --- Bulk Delete ---

  get selectedCount(): number { return this.selectedIds.size; }
  isSelected(id: number): boolean { return this.selectedIds.has(id); }

  toggleSelect(id: number, event: Event): void {
    event.stopPropagation();
    if (this.selectedIds.has(id)) { this.selectedIds.delete(id); }
    else { this.selectedIds.add(id); }
    this.selectedIds = new Set(this.selectedIds);
  }

  get allVisibleSelected(): boolean {
    return this.customers.length > 0 && this.customers.every(c => this.selectedIds.has(c.customerId));
  }

  toggleSelectAll(): void {
    if (this.allVisibleSelected) {
      for (const c of this.customers) { this.selectedIds.delete(c.customerId); }
    } else {
      for (const c of this.customers) { this.selectedIds.add(c.customerId); }
    }
    this.selectedIds = new Set(this.selectedIds);
  }

  requestBulkDelete(): void { this.showBulkDeleteConfirm = true; }
  cancelBulkDelete(): void { this.showBulkDeleteConfirm = false; }

  confirmBulkDelete(): void {
    this.isBulkDeleting = true;
    const ids: number[] = [...this.selectedIds];
    executeBulkDelete(ids, (id: number) => this._adminConfigService.deleteCustomer(id))
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (result) => {
          const toast = formatBulkDeleteToast(result, 'customer');
          this.toastNotification.showToast(toast.message, toast.type === 'success' ? ACS_TOAST_SUCCESS_DURATION : ACS_TOAST_ERROR_DURATION, toast.type);
          this.selectedIds = new Set();
          this.showBulkDeleteConfirm = false;
          this.isBulkDeleting = false;
          this.searchCustomers();
        }
      });
  }

}
